import { Grid, useMediaQuery } from "@mui/material";
import { Create, Delete } from "@mui/icons-material";

import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { IMerchant, MerchantStatus } from "@framework/types";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { FormGrid, List } from "@dzambalaorg/ui-kit";

import { OfficeDialogKeys } from "../../../shared";
import { merchantsValidationSchema } from "./form/validation";

const MERCHANTS_URL = "/merchants";

export const Merchant = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IMerchant>
  >(MERCHANTS_URL, { enableInfiniteScroll: isMd }, void 0, {
    merchantStatus: [MerchantStatus.ACTIVE],
  });

  const { openDialog } = useGlobalDialogFormCallbacks<IMerchant, IMerchant>();
  const { handleDelete } = useDialogDelete<IMerchant>();

  const merchantEdit = (merchant: IMerchant) => () => {
    openDialog(OfficeDialogKeys.MERCHANTS, merchant, {
      refetch,
      initialValues: merchant,
      message: "dialogs.edit",
      testId: "MerchantFormDialog",
      url: `${MERCHANTS_URL}/${merchant.id}`,
      validationSchema: merchantsValidationSchema,
      filter: ({ id: _id, ...rest }) => rest,
      prepareInitialValues: ({ id, title, description, email, imageUrl, merchantStatus, wallet, ratePlan }) => ({
        id,
        title,
        description,
        email,
        merchantStatus,
        ratePlan,
        imageUrl,
        wallet,
      }),
      componentProps: {
        mode: "edit",
      },
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "merchants"]} />

      <PageHeader message="pages.merchants.title" />

      <SearchWithFilters
        initialValues={{
          merchantStatus: [MerchantStatus.ACTIVE],
        }}
      >
        <FormGrid columns={[1]}>
          <SelectInput multiple name="merchantStatus" options={MerchantStatus} />
        </FormGrid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(merchant => (
            <StyledListItem key={merchant.id}>
              <List.ItemText>{merchant.title}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={merchantEdit(merchant)}
                  message="form.buttons.edit"
                  dataTestId="MerchantEditButton"
                  icon={Create}
                />
                <ListAction
                  onClick={handleDelete(merchant)}
                  message="form.buttons.delete"
                  dataTestId="MerchantDeleteButton"
                  icon={Delete}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={MERCHANTS_URL} refetch={refetch} />
    </Grid>
  );
};
